"use client";

import { SiteLink } from "@/components/shared/SiteLink/SiteLink";
import styles from "./HeaderMenu.module.scss";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import IconCross from "@/images/icons/cross.svg";
import IconCaret from "@/images/icons/caret-down.svg";
import IconArrowLongLeft from "@/images/icons/arrow-long-left.svg";
import clsx from "clsx";
import { breakpoint } from "@/utilities/breakpoints";
import { useBreakpoint } from "@/hooks/useViewportDimensions";
import { ButtonLink } from "@/components/shared/Button/Button";
import { EmergencyNumber } from "@/components/shared/EmergencyNumber/EmergencyNumber";

export type NavItem = {
	id: string;
	label: string;
	href: string;
};

export type NavParent = {
	id: string;
	label: string;
	children: Array<NavItem>;
};
export namespace HeaderMenu {
	export type Props = {
		navItems: Array<NavParent | NavItem>;
		className?: string;
		isMobileExpanded: boolean;
		setIsMobileExpanded: (v: boolean) => void;
	};
}

export const HeaderMenu = ({
	navItems,
	className,
	isMobileExpanded,
	setIsMobileExpanded,
}: HeaderMenu.Props) => {
	const [expandedNavItem, setExpandedNavItem] = useState<string | null>(null);
	const isMobile = !useBreakpoint("xl", true);

	useEffect(() => {
		if (typeof document !== "undefined") {
			document.documentElement.classList.toggle("prevent-scroll", isMobile && isMobileExpanded);
		}
	}, [isMobileExpanded, isMobile]);

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (expandedNavItem) {
				if (
					(event.target as HTMLElement).closest(`[data-parent-wrapper="${expandedNavItem}"]`) ===
						null &&
					breakpoint("lg")
				) {
					setExpandedNavItem(null);
				}
			}
		}

		if (expandedNavItem) {
			if (menuRef.current) {
				menuRef.current.scrollTop = 0;
			}
			document.addEventListener("click", handleClickOutside);
		}
		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, [expandedNavItem]);

	useEffect(() => {
		if (typeof document !== "undefined") {
			function onScroll() {
				setExpandedNavItem(null);
			}

			if (!isMobile && expandedNavItem) {
				document.body.addEventListener("scroll", onScroll);
			}

			return () => {
				document.body.removeEventListener("scroll", onScroll);
			};
		}
	}, [expandedNavItem, isMobile]);

	const menuRef = useRef<HTMLDivElement>(null);

	return (
		<motion.div
			ref={menuRef}
			className={clsx(
				styles["header-menu"],
				className,
				isMobile && expandedNavItem && "overflow-hidden",
				isMobileExpanded && styles["header-menu--mobile-expanded"],
			)}
			style={{ transition: isMobile ? "0.4s ease-in-out" : undefined }}
		>
			<div className={styles["header-menu__mobile-header"]}>
				<button
					className={styles["header-menu__close"]}
					onClick={() => {
						setIsMobileExpanded(false);
						setExpandedNavItem(null);
					}}
				>
					<span className="sr-only">Close Navigation Menu</span>
					<IconCross />
				</button>
			</div>
			<ul className={styles["header-menu__list"]}>
				{navItems.map((item, index) => (
					<li
						className={styles["header-menu__item"]}
						key={index}
					>
						{"href" in item && (
							<SiteLink
								className={clsx(styles["header-menu__parent"])}
								href={item.href}
							>
								{item.label}
							</SiteLink>
						)}
						{"children" in item && (
							<div
								className={styles["header-menu__parent-wrapper"]}
								data-parent-wrapper={item.id}
							>
								<button
									className={clsx(
										styles["header-menu__parent"],
										expandedNavItem === item.id && styles["header-menu__parent--expanded"],
									)}
									onClick={() => setExpandedNavItem(expandedNavItem === item.id ? null : item.id)}
									aria-expanded={expandedNavItem === item.id ? "true" : "false"}
									aria-haspopup="menu"
								>
									{item.label}
									<IconCaret className={styles["header-menu__parent-caret"]} />
								</button>
								<AnimatePresence>
									{expandedNavItem === item.id && (
										<motion.div
											className={styles["header-submenu"]}
											initial={isMobile ? { transform: "translateX(100%)" } : { opacity: 0 }}
											animate={isMobile ? { transform: "translateX(0%)" } : { opacity: 1 }}
											exit={isMobile ? { transform: "translateX(100%)" } : { opacity: 0 }}
											transition={{ duration: isMobile ? 0.4 : 0.2, ease: "easeInOut" }}
										>
											<button
												className={styles["header-submenu__back"]}
												onClick={() => setExpandedNavItem(null)}
												aria-label=""
											>
												<IconArrowLongLeft />
											</button>
											<div className={styles["header-submenu__heading"]}>{item.label}</div>
											<ul
												className={styles["header-submenu__list"]}
												aria-label={item.label}
											>
												{item.children.map((child, index) => (
													<li
														className={styles["header-submenu__item"]}
														key={index}
													>
														<SiteLink
															className={styles["header-submenu__link"]}
															href={child.href}
														>
															<div className={styles["header-submenu__link-inner"]}>
																{child.label}
																<IconArrowLongLeft />
															</div>
														</SiteLink>
													</li>
												))}
											</ul>
										</motion.div>
									)}
								</AnimatePresence>
							</div>
						)}
					</li>
				))}
			</ul>

			<div className={styles["header-menu__footer"]}>
				{/* <div className={styles["header-menu__footer-actions"]}>
				</div> */}
				<EmergencyNumber />
				<div className={styles["header-menu__footer-links"]}>
					<SiteLink
						className={styles["header-menu__link"]}
						href={"https://tasgasnetworks.com.au/network-alerts"}
					>
						Gas Network Alerts
					</SiteLink>
					<div className={styles["header-menu__v-bar"]} />
					<SiteLink
						className={styles["header-menu__link"]}
						href={"/contact-us"}
					>
						Contact us
					</SiteLink>
				</div>
			</div>
		</motion.div>
	);
};
